/* ==============================================================================
   Ícone de servidor
   ==============================================================================
   Existe como componente próprio porque o mesmo servidor precisa ser reconhecido
   em TRÊS lugares ao mesmo tempo — o círculo da barra de comando, o cabeçalho da
   lateral e a barra superior. Um ícone que aparece num lugar e não no outro não
   vira referência visual; vira decoração intermitente.

   Duas coisas que precisam ser IGUAIS nos três, e é por isso que o código é um só:

     · o gradiente do fallback, derivado do nome por FNV-1a. Determinístico de
       propósito: `Math.random` mudaria a cor a cada render e a cada dispositivo,
       e uma cor que muda não identifica nada.
     · o comportamento de falha. Imagem que não carrega cai nas iniciais, não no
       ícone de imagem quebrada do navegador.

   Sobre GIF animado: o `<img>` simples anima sozinho, e está bom. Poster estático
   com animação no hover exige um segundo arquivo extraído no upload — é o que o
   `Avatar` faz, porque numa lista de 200 membros o custo de decode é mensurável.
   Aqui há no máximo um ícone por lugar, e a infraestrutura não se paga.
   ============================================================================== */

import { useState } from 'react';

/** Raio por papel: quadrado arredondado em cabeçalho, círculo em fileira. */
export type GuildIconShape = 'squircle' | 'circle';

export interface GuildIconProps {
  name: string;
  /** URL pronta para `<img src>`. `null` cai nas iniciais. */
  iconUrl?: string | null | undefined;
  size?: number;
  shape?: GuildIconShape;
  /** Anel de aresta e sombra. Desligue quando o pai já desenha a moldura. */
  framed?: boolean;
  /**
   * Pintar o gradiente por trás das iniciais.
   *
   * `false` deixa o fundo do PAI aparecer, e é o que a barra de comando precisa:
   * ali os círculos são preenchidos com o violeta de acento, e um gradiente
   * sorteado por cima quebraria a fileira — cinco cores diferentes onde a
   * referência mostra cinco círculos da mesma cor. Com imagem, o gradiente nunca
   * é usado de todo jeito.
   */
  fallbackFill?: boolean;
}

export function GuildIcon({
  name,
  iconUrl,
  size = 24,
  shape = 'squircle',
  framed = true,
  fallbackFill = true,
}: GuildIconProps) {
  const [failed, setFailed] = useState(false);
  const show = iconUrl != null && iconUrl.length > 0 && !failed;

  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center overflow-hidden"
      style={{
        width: size,
        height: size,
        borderRadius:
          shape === 'circle' ? 'var(--radius-full)' : 'var(--radius-md)',
        background: show
          ? 'var(--color-surface-sunken)'
          : fallbackFill
            ? guildGradient(name)
            : 'transparent',
        boxShadow: framed
          ? 'inset 0 1px 0 oklch(100% 0 0 / 0.16), 0 1px 4px oklch(0% 0 0 / 0.30)'
          : 'none',
      }}
    >
      {show ? (
        <img
          src={iconUrl}
          alt=""
          className="size-full object-cover"
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          className="font-bold leading-none"
          style={{
            /* 40% da caixa: legível em 20px e proporcional em 44px. Abaixo de
               9px duas letras deixam de ser distinguíveis. */
            fontSize: Math.max(9, Math.round(size * 0.4)),
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--color-on-solid)',
            textShadow: 'var(--shadow-text)',
          }}
        >
          {guildInitials(name)}
        </span>
      )}
    </span>
  );
}

export function guildInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Gradiente determinístico a partir do nome.
 *
 * A luminosidade 52%/42% não é escolha estética: varrendo os 360 matizes, o pior
 * caso para texto branco (ciano-verde, ~190) dava 3.65:1 em 58%. Em 52% o pior
 * caso passa a 4.62:1, então TODO matiz sorteado atende AA com as iniciais. É a
 * mesma calibração do fallback do `Avatar`, e a razão de os dois usarem os mesmos
 * números.
 */
export function guildGradient(seed: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const value = hash >>> 0;
  const hue = value % 360;
  const shift = 40 + (value % 50);
  return `linear-gradient(135deg, oklch(52% 0.16 ${hue}) 0%, oklch(42% 0.17 ${
    (hue + shift) % 360
  }) 100%)`;
}
